// Import des APIs
import { DocumentApi } from '../../api/Document';
import { AgentApi } from '../../api/Agent';

import { navigation as navigationObject } from '../../lib/Navigation/Navigation.component'
import { useSessionStore } from '../../lib/Store/sessionStore.component';

import { NotifyType } from '../../components/Notification/Notification.types';
import { CustomFile } from '../../api/Document/types';

export const ProfilActions = {
  // Récupération de l'identifiant de la pièce jointe photo
  getIdentifiantPJ: async (): Promise<number | null> => {
    try {
      const { currentLogin } = useSessionStore.getState().getCurrentInfos();
      const params = `?type=PhotoAgent&identifiantOrigine=${currentLogin?.identifiant}`;
      const response: any = await DocumentApi.loadDocument(params);
      
      // L'API peut retourner {data: Array, links: {...}, meta: {...}}
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        const identifiant = response.data[0]?.identifiant;
        return identifiant || null;
      }
      
      // Ou retourner directement un tableau
      if (Array.isArray(response) && response.length > 0 && response[0]?.identifiant) {
        const identifiant = response[0].identifiant;
        return identifiant;
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching PJ identifiant:", error);
      return null;
    }
  },

  // Récupération de la photo de l'agent
  getPhoto: async (): Promise<string | null> => {
    try {
      const identifiantPJ = await ProfilActions.getIdentifiantPJ();
      
      if (identifiantPJ) {
        const photoUrl = await DocumentApi.downloadDocument(identifiantPJ);
        return photoUrl;
      }
      return null;
    } catch (error) {
      console.error('Error fetching photo:', error);
      return null;
    }
  },

  // Gestion de la modification du mot de passe
  handleModificationMDP: (navigation: any) => {
      navigationObject.navigate("", navigation,{showPasswordReset: true});
  },

  // Mise à jour des emails de l'agent
  updateEmail: async (
    email: string,
    emailType: 'personnel' | 'professionnel',  
    notify?: NotifyType
  ) => {
    try {
      const { currentLogin } = useSessionStore.getState().getCurrentInfos();
      if (!currentLogin?.identifiant) {
        throw new Error('Session utilisateur non trouvée');
      }

      // Préparer les données pour l'API selon le type d'email
      const updateData = emailType === 'personnel' 
        ? { emailPersonnel: email }
        : { emailProfessionnel: email };

      // Mise à jour via l'API Agent
      await AgentApi.updateAgent(currentLogin.identifiant, updateData);

      // Mettre à jour les loginInfo dans le sessionStore
      useSessionStore.getState().updateCurrentLoginInfo(updateData);

      notify?.success(`Email ${emailType} mis à jour avec succès`);
      
    } catch (error) {
      notify?.error(`Erreur lors de la mise à jour de l'email ${emailType}`);    
      throw error;
    }
  },

  // Mise à jour de l'entité par défaut de l'agent
  updateEntiteDefaut: async (
    identifiantEntite: number,    
    notify?: NotifyType
  ) => {
    try {
      const sessionStore = useSessionStore.getState();
      const { currentLogin } = sessionStore.getCurrentInfos();
      if (!currentLogin?.identifiant) {
        throw new Error('Session utilisateur non trouvée');
      }

      // Mise à jour via l'API Agent
      await AgentApi.updateAgent(currentLogin.identifiant, {
        identifiantEntiteDefautEsag: identifiantEntite
      });

      // Mettre à jour les loginInfo dans le sessionStore
      sessionStore.updateCurrentLoginInfo({
        identifiantEntiteDefautEsag: identifiantEntite
      });
      
      notify?.success('Entité par défaut mise à jour avec succès');
      
    } catch (error) {      
      notify?.error('Erreur lors de la mise à jour de l\'entité par défaut');
      throw error; // Re-throw pour que le composant puisse gérer l'erreur
    }
  },

  // Mise à jour de la photo de profil (Web et Mobile)
  updatePhoto: async (
    file: File | CustomFile,
    setContenuErreur?: (erreur: { creation: string }) => void,
    notify?: NotifyType
  ) => {
    const { getCurrentInfos, updateCurrentLoginInfo } = useSessionStore.getState();
    const {currentLogin} = getCurrentInfos()
    
    try {
      // 1. Vérifier s'il existe déjà une photo et la supprimer si c'est le cas
      const existingPhotoId = await ProfilActions.getIdentifiantPJ();
      if (existingPhotoId) {       
        try {
          await DocumentApi.deleteDocument(existingPhotoId);       
        } catch (deleteError) {
          console.warn('⚠️ Erreur lors de la suppression de l\'ancienne photo:', deleteError);
          // On continue quand même pour essayer d'ajouter la nouvelle
        }
      } else {
        console.log('ℹ️ Aucune photo existante trouvée, ajout de la première photo');
      }

      // 2. Ajouter la nouvelle photo
      await DocumentApi.postDocument(
        file,
        {
          type: 'PhotoAgent',
          identifiantOrigine: currentLogin?.identifiant,
          identifiantEntite: currentLogin?.identifiantEntiteDefautEsag,
          identifiantLogin: currentLogin?.identifiant
        }
      );
      
      // 3. Mise à jour de l'image dans le state après succès
      if (file instanceof File) {
        // Web : utiliser FileReader pour convertir en data URL
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          updateCurrentLoginInfo({imageProfil: result})
        };
        reader.readAsDataURL(file);
      } else {
        // Mobile : re-fetch depuis l'API pour avoir une data URI stable
        // (les URI content:// ou file:// locaux deviennent invalides après redémarrage)
        ProfilActions.getPhoto().then((imageData) => {
          if (imageData) updateCurrentLoginInfo({imageProfil: imageData});
        });
      }
      
      notify?.success('Photo de profil mise à jour avec succès');
      
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de la photo:', error);
      notify?.error('Erreur lors de l\'enregistrement de la photo');
      if (setContenuErreur) {
        setContenuErreur({ creation: 'Erreur lors de l\'enregistrement de la photo' });
      }
    }
  },

  // Gestion du choix d'image depuis la galerie
  // Retourne les callbacks pour le composant DocumentPicker
  createImagePickerCallbacks: (
    toggleDialog: () => void,
    setContenuErreur?: (erreur: { creation: string }) => void,
    notify?: NotifyType
  ) => ({
    onFilePicked: async (selectedFile: any) => {
      // Fermer la modal immédiatement après la sélection
      toggleDialog();
      
      try {
        const fileForUpload = {
          uri: selectedFile.uri,
          type: selectedFile.type || 'image/jpeg',
          name: selectedFile.name || 'image.jpg',
        };
        
        await ProfilActions.updatePhoto(fileForUpload, setContenuErreur, notify);
      } catch (error) {
        console.error('Erreur lors du traitement de l\'image:', error);
        notify?.error('Erreur lors du traitement de l\'image');
        if (setContenuErreur) {
          setContenuErreur({ creation: 'Erreur lors du traitement de l\'image' });
        }
      }
    },
    onError: (error: Error) => {
      console.error('Erreur lors de la sélection de l\'image:', error);
      notify?.error('Erreur lors de la sélection de l\'image');
      if (setContenuErreur) {
        setContenuErreur({ creation: 'Erreur lors de la sélection de l\'image' });
      }
    },
    onCancel: () => {
      console.log('Sélection d\'image annulée');
      // Ne pas fermer la modal, laisser l'utilisateur choisir une autre option
    }
  }),

  // Mise à jour de l'IBAN de l'agent
  updateIBAN: async (
    iban: string,
    notify?: NotifyType
  ) => {
    try {
      const { currentLogin } = useSessionStore.getState().getCurrentInfos();
      if (!currentLogin?.identifiant) throw new Error('Session utilisateur non trouvée');

      await AgentApi.updateAgent(currentLogin.identifiant, { iban });
      useSessionStore.getState().updateCurrentLoginInfo({ iban });
      notify?.success('IBAN enregistré');
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l’IBAN :', error);
      notify?.error('Erreur lors de la sauvegarde de l\'IBAN');
      throw error;
    }
  },

  // Mise à jour texte de la signature
  updateSignatureText: async (
    signature: string,
    notify?: NotifyType
  ) => {
    try {
      const { currentLogin } = useSessionStore.getState().getCurrentInfos();
      if (!currentLogin?.identifiant) throw new Error('Session utilisateur non trouvée');

      await AgentApi.updateAgent(currentLogin.identifiant, { signature } as any);
      useSessionStore.getState().updateCurrentLoginInfo({ signature });
      notify?.success('Signature enregistrée');
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la signature :', error);
      notify?.error('Erreur lors de la sauvegarde de la signature');
      throw error;
    }
  },

  // Upload d'une image de signature
  // Retourne la data URI du document uploadé (ou null)
  uploadSignatureImage: async (
    file: File | any,
    notify?: NotifyType
  ): Promise<string | null> => {
    try {
      const { currentLogin } = useSessionStore.getState().getCurrentInfos();
      if (!currentLogin?.identifiant) throw new Error('Session utilisateur non trouvée');

      // Supprimer l'ancienne signature si elle existe
      const existingId = await ProfilActions.getIdentifiantDocumentByType('Signature');
      if (existingId) {
        try { await DocumentApi.deleteDocument(existingId); } catch (_) {}
      }

      const doc = await DocumentApi.postDocument(file, {
        type: 'Signature',
        identifiantOrigine: currentLogin.identifiant,
        identifiantEntite: currentLogin.identifiantEntiteDefautEsag,
        identifiantLogin: currentLogin.identifiant,
      });

      // On ne stocke PAS la data URI volumineuse dans le sessionStore
      // pour éviter les re-renders en cascade et les crashes mémoire.
      notify?.success('Signature image enregistrée');
      return doc?.identifiant ? String(doc.identifiant) : null;
    } catch (error) {
      console.error('Erreur lors de l\'upload de la signature :', error);
      notify?.error('Erreur lors de l\'upload de la signature');
      throw error;
    }
  },

  // Callbacks pour le DocumentPicker pour la signature
  createSignaturePickerCallbacks: (
    toggleModal: () => void,
    notify?: NotifyType
  ) => ({
    onFilePicked: async (selectedFile: any) => {
      toggleModal();
      try {
        const fileForUpload = {
          uri: selectedFile.uri,
          type: selectedFile.type || 'image/jpeg',
          name: selectedFile.name || 'signature.jpg',
        };
        await ProfilActions.uploadSignatureImage(fileForUpload, notify);
      } catch (error) {
        console.error('Erreur lors du traitement de la signature image:', error);
        notify?.error('Erreur lors du traitement de la signature');
      }
    },
    onError: (error: Error) => {
      console.error('Erreur selection signature:', error);
      notify?.error('Erreur lors de la sélection de la signature');
    },
    onCancel: () => {
      console.log('Sélection signature annulée');
    }
  }),

  // Upload d'un RIB (pdf ou image)
  // Retourne l'identifiant du document uploadé (ou null)
  uploadRibFile: async (
    file: File | any,
    notify?: NotifyType
  ): Promise<string | null> => {
    try {
      const { currentLogin } = useSessionStore.getState().getCurrentInfos();
      if (!currentLogin?.identifiant) throw new Error('Session utilisateur non trouvée');

      const doc = await DocumentApi.postDocument(file, {
        type: 'RIB',
        identifiantOrigine: currentLogin.identifiant,
        identifiantEntite: currentLogin.identifiantEntiteDefautEsag,
        identifiantLogin: currentLogin.identifiant,
      });

      // On ne stocke PAS la data URI volumineuse dans le sessionStore
      // pour éviter les re-renders en cascade et les crashes mémoire.
      notify?.success('IBAN enregistré');
      return doc?.identifiant ? String(doc.identifiant) : null;
    } catch (error) {
      console.error('Erreur lors de l\'upload de l\'IBAN :', error);
      notify?.error('Erreur lors de l\'upload de l\'IBAN');
      throw error;
    }
  },

  // Suppression d'un RIB existant
  deleteRibDocument: async (
    notify?: NotifyType
  ) => {
    try {
      const id = await ProfilActions.getIdentifiantDocumentByType('RIB');
      if (!id) {
        notify?.error('Aucun IBAN trouvé');
        return;
      }
      await DocumentApi.deleteDocument(id);
      useSessionStore.getState().updateCurrentLoginInfo({ iban: undefined });
      notify?.success('IBAN supprimé');
    } catch (error) {
      console.error('Erreur lors de la suppression de l`IBAN :', error);
      notify?.error('Erreur lors de la suppression de l`IBAN');
      throw error;
    }
  },

  // Callbacks pour le DocumentPicker pour le RIB
  createRibPickerCallbacks: (
    toggleModal: () => void,
    notify?: NotifyType
  ) => ({
    onFilePicked: async (selectedFile: any) => {
      toggleModal();
      try {
        const fileForUpload = {
          uri: selectedFile.uri,
          type: selectedFile.type || 'application/pdf',
          name: selectedFile.name || 'rib.pdf',
        };
        await ProfilActions.uploadRibFile(fileForUpload, notify);
      } catch (error) {
        console.error('Erreur lors du traitement de l`IBAN :', error);
        notify?.error('Erreur lors du traitement de l`IBAN');
      }
    },
    onError: (error: Error) => {
      console.error('Erreur selection de l`IBAN:', error);
      notify?.error('Erreur lors de la sélection de l`IBAN');
    },
    onCancel: () => {
      console.log('Sélection IBAN annulée');
    }
  }),

  // Gestion de la prise de photo via le composant Camera
  // Retourne les callbacks pour le composant Camera
  createPhotoPickerCallbacks: (
    toggleDialog: () => void,
    setContenuErreur?: (erreur: { creation: string }) => void,
    notify?: NotifyType
  ) => ({
    onPhotoCaptured: async (capturedPhoto: any) => {
      // Fermer la modal immédiatement après la capture
      toggleDialog();
      
      try {
        // Pour web et mobile : traiter la photo capturée
        if (typeof window !== 'undefined' && capturedPhoto.uri.startsWith('data:')) {
          // Web : convertir data URL en File
          const response = await fetch(capturedPhoto.uri);
          const blob = await response.blob();
          const file = new File([blob], capturedPhoto.name || 'camera_photo.jpg', {
            type: capturedPhoto.type || 'image/jpeg'
          });

          const {currentLogin} = useSessionStore.getState().getCurrentInfos();
          if (currentLogin) {
            await ProfilActions.updatePhoto(file, setContenuErreur, notify);
          }
        } else {
          // Mobile : uploader la photo, puis re-fetch depuis l'API pour une data URI stable
          const {currentLogin} = useSessionStore.getState().getCurrentInfos();
          if (currentLogin) {
            try {
              const fileForUpload = {
                uri: capturedPhoto.uri,
                type: capturedPhoto.type || 'image/jpeg',
                name: capturedPhoto.name || 'camera_photo.jpg',
              };
              
              await ProfilActions.updatePhoto(fileForUpload, setContenuErreur, notify);
            } catch (error) {
              console.error('Erreur lors de l\'upload photo mobile:', error);
            }
          }
        }
      } catch (error) {
        console.error('Erreur lors du traitement de la photo:', error);
        notify?.error('Erreur lors du traitement de la photo');
        if (setContenuErreur) {
          setContenuErreur({ creation: 'Erreur lors du traitement de la photo' });
        }
      }
    },
    onError: (error: Error) => {
      console.error('Erreur lors de la prise de photo:', error);
      notify?.error('Erreur lors de la prise de photo');
      if (setContenuErreur) {
        setContenuErreur({ creation: 'Erreur lors de la prise de photo' });
      }
    },
    onCancel: () => {
      console.log('Prise de photo annulée');
      // Ne pas fermer la modal, laisser l'utilisateur choisir une autre option
    }
  }),

    // Récupération de l'identifiant d'un document selon son type (ex: Signature, RIB)
    getIdentifiantDocumentByType: async (type: string): Promise<number | null> => {
      try {
        const { currentLogin } = useSessionStore.getState().getCurrentInfos();
        if (!currentLogin?.identifiant) return null;

        const params = `?type=${encodeURIComponent(type)}&identifiantOrigine=${currentLogin.identifiant}`;
        const response: any = await DocumentApi.loadDocument(params);

        if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
          return response.data[0]?.identifiant || null;
        }

        if (Array.isArray(response) && response.length > 0 && response[0]?.identifiant) {
          return response[0].identifiant;
        }

        return null;
      } catch (error) {
        console.error(`Erreur getIdentifiantDocumentByType(${type}):`, error);
        return null;
      }
    },

    // Téléchargement d'un document par type (retourne une data URI ou URL selon plateforme)
    fetchDocumentByType: async (type: string): Promise<string | null> => {
      try {
        const id = await ProfilActions.getIdentifiantDocumentByType(type);
        if (!id) return null;
        const dataUri = await DocumentApi.downloadDocument(id);
        return dataUri || null;
      } catch (error) {
        console.error(`Erreur fetchDocumentByType(${type}):`, error);
        return null;
      }
    },

    // Charge IBAN + documents Signature / RIB
    // Retourne les data URIs sans les stocker dans le sessionStore
    // (les data URIs volumineuses ne doivent PAS être dans le store Zustand
    //  pour éviter les re-renders en cascade et les crashes mémoire).
    loadAgentDocumentsAndIban: async (): Promise<{ iban?: string | null; signature?: string | null } | null> => {
      try {
        const { currentLogin } = useSessionStore.getState().getCurrentInfos();
        if (!currentLogin?.identifiant) return null;

        const [signature, iban] = await Promise.all([
          ProfilActions.fetchDocumentByType('Signature'),
          ProfilActions.fetchDocumentByType('RIB')
        ]);

        return { iban: iban || null, signature: signature || null };
      } catch (error) {
        console.error('Erreur lors du chargement des documents/IBAN :', error);
        return null;
      }
    }
  };
